import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";
import { AxiosError } from "axios";

import {
  OtpInput,
  PennButton,
  PennField,
  PennInput,
  PennLink,
  PennText,
} from "@/components/ui";

import {
  fetchRegistrationStatus,
  signinPrecheckRequest,
} from "@penn/web-common";
import { useAppDispatch } from "../../stores/hooks";
import {
  ENFORCE_WEB_APP_CONTEXT,
  OIDC_ENABLED,
} from "@penn/web-common";
import {
  initiateOtpSignIn,
  resendOtp,
  submitOtp,
} from "@penn/web-common";
import { completePennSession } from "@penn/web-common";
import { OidcLoginPage } from "./OidcLoginPage";

const OTP_LENGTH = 8;
const RESEND_COOLDOWN_S = 30;

const emailSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "Email is required.")
    .email("Invalid email."),
});
type EmailFormData = z.infer<typeof emailSchema>;

interface LocationState {
  from?: { pathname?: string };
  /** Set by RegisterPage when Amplify auto-sign-in cannot continue. */
  email?: string;
  registered?: boolean;
}

interface ApiError {
  error?: { code?: string; message?: string };
}

function authErrorCode(err: unknown): string | undefined {
  if (err instanceof AxiosError) {
    return (err.response?.data as ApiError | undefined)?.error?.code;
  }
  return undefined;
}

/**
 * Issue 9: a brand-new (never-registered) user has no Cognito identity, so the
 * email step (`initiateOtpSignIn`) throws a Cognito `UserNotFoundException`
 * BEFORE the backend `user-not-registered` (which only fires after OTP, for a
 * verified identity with no DB row). Detect both so any unregistered user is
 * routed to /register instead of a dead-end error. (When the pool has
 * preventUserExistenceErrors enabled, Cognito returns a challenge instead of
 * throwing — that path still falls through to the post-OTP backend check.)
 */
function isCognitoUserNotFound(err: unknown): boolean {
  if (err instanceof AxiosError) return false;
  const name = (err as { name?: string })?.name ?? "";
  const message = err instanceof Error ? err.message : String(err ?? "");
  return (
    name === "UserNotFoundException" ||
    /UserNotFoundException/i.test(message) ||
    /user.*does not exist/i.test(message)
  );
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as ApiError | undefined;
    const code = data?.error?.code;
    if (code === "wrong-app-for-account") {
      // PENN-094: "admin portal" → "internal app portal"
      return "This is an internal staff account. Please sign in via the internal app portal.";
    }
    if (code === "not-an-internal-account") {
      return (
        data?.error?.message ??
        "This is a customer account. Please sign in via the customer portal."
      );
    }
    if (code === "user-not-registered") {
      // Issue 4: verified identity has no DB account.
      return "No account is registered for this email. Please register first.";
    }
    if (code === "invalid-credentials") {
      return "Sign-in could not be completed. Please check your details and try again.";
    }
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the API gateway.";
    }
    return data?.error?.message ?? "Sign-in failed. Please try again.";
  }
  if (err instanceof Error) return err.message;
  return "Sign-in failed. Please try again.";
}

// The app-local Cognito email-OTP sign-in (AUTH_MODE=cognito, the default).
const CognitoLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const locationState = location.state as LocationState | null;
  const returnTo = locationState?.from?.pathname ?? "/";
  // Registration fallback hands off the just-registered email so the user only
  // has to trigger their login OTP.
  const prefillEmail = locationState?.email ?? "";

  const [step, setStep] = useState<"email" | "otp">("email");
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [registeredNotice, setRegisteredNotice] = useState<boolean>(
    locationState?.registered === true,
  );

  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const otpAutoSubmittedRef = useRef(false);

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: prefillEmail },
  });

  // Issue 4/9: an unregistered email is steered to /register (prefilling the
  // email) instead of a dead-end error or a fake OTP prompt.
  const redirectToRegister = (email: string) => {
    setSubmitError(
      "No account is registered for this email. Redirecting you to register…",
    );
    window.setTimeout(() => navigate("/register", { state: { email } }), 1400);
  };

  const handleAuthError = (err: unknown, email: string) => {
    if (
      authErrorCode(err) === "user-not-registered" ||
      isCognitoUserNotFound(err)
    ) {
      redirectToRegister(email);
      return;
    }
    setSubmitError(extractErrorMessage(err));
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const onSubmitEmail = async (data: EmailFormData) => {
    setSubmitError(null);
    setRegisteredNotice(false);
    setWorking(true);
    setPendingEmail(data.email);
    try {
      // Cross-app pre-flight BEFORE OTP. Customer app refuses internal/staff
      // accounts with 403 wrong-app-for-account; internal app refuses customer
      // accounts with not-an-internal-account. Only those 403s stop sign-in;
      // any other failure fails open (the /session gate backstops). Behind
      // ENFORCE_WEB_APP_CONTEXT (default true).
      if (ENFORCE_WEB_APP_CONTEXT) {
        try {
          await signinPrecheckRequest(data.email);
        } catch (err) {
          const code = authErrorCode(err);
          if (
            code === "wrong-app-for-account" ||
            code === "not-an-internal-account"
          ) {
            throw err;
          }
        }
      }
      // Issue 9 (robust): ask our own DB whether this email has an ACTIVE
      // account BEFORE sending a code. With preventUserExistenceErrors on,
      // Cognito returns a fake challenge for unknown emails (so the old
      // isCognitoUserNotFound throw-check never fires and the user sees
      // "OTP sent"). Redirect unregistered emails to /register instead.
      // Fail-open: if the status check itself errors (network/gateway blip),
      // fall through to Cognito + the post-OTP backend check so a transient
      // outage never blocks a legitimate sign-in.
      try {
        const { registered } = await fetchRegistrationStatus(data.email);
        if (!registered) {
          redirectToRegister(data.email);
          return;
        }
      } catch {
        /* status probe failed — continue to OTP, post-OTP check backstops */
      }
      const out = await initiateOtpSignIn(data.email);
      if (out.mode === "done") {
        await completeExchange();
        return;
      }
      setOtp("");
      otpAutoSubmittedRef.current = false;
      setStep("otp");
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      handleAuthError(err, data.email);
    } finally {
      setWorking(false);
    }
  };

  const verifyOtp = async (code: string) => {
    if (working) return;
    if (code.length !== OTP_LENGTH || !/^\d+$/.test(code)) {
      setSubmitError(`Enter the ${OTP_LENGTH}-digit code.`);
      return;
    }
    setSubmitError(null);
    setWorking(true);
    try {
      await submitOtp(code);
      await completeExchange();
    } catch (err) {
      otpAutoSubmittedRef.current = false;
      handleAuthError(err, pendingEmail);
    } finally {
      setWorking(false);
    }
  };

  const handleOtpChange = (next: string) => {
    setOtp(next);
    if (submitError) setSubmitError(null);
  };

  const handleOtpComplete = (code: string) => {
    if (otpAutoSubmittedRef.current) return;
    otpAutoSubmittedRef.current = true;
    void verifyOtp(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || working || !pendingEmail) return;
    setResendNotice(null);
    setSubmitError(null);
    setWorking(true);
    try {
      await resendOtp(pendingEmail);
      setOtp("");
      otpAutoSubmittedRef.current = false;
      setResendCooldown(RESEND_COOLDOWN_S);
      setResendNotice(`A new code was sent to ${pendingEmail}.`);
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  async function completeExchange(): Promise<void> {
    await completePennSession({
      email: pendingEmail,
      dispatch,
      navigate,
      returnTo,
      signinPrechecked: true,
    });
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-soft px-8 py-10">
          <div className="text-center mb-4">
            <PennText as="h1" size="2xl" color="blue_grey1" font="bold">
              Sign in
            </PennText>
            <PennText as="p" size="sm" color="blue_grey1" className="mt-1">
              {step === "email"
                ? "We'll email you a one-time code."
                : `We sent an ${OTP_LENGTH}-digit code to ${pendingEmail}.`}
            </PennText>
            <PennText as="p" size="sm" color="blue_grey" className="mt-1">
              {step === "email"
                ? ""
                : `Didn't get it? Please check your spam or junk folder.`}
            </PennText>
          </div>

          {step === "email" && (
            <form
              onSubmit={emailForm.handleSubmit(onSubmitEmail)}
              noValidate
              className="space-y-5"
            >
              {registeredNotice && !submitError && (
                <div
                  role="status"
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                >
                  Your account is ready. Sign in below to get your one-time
                  code.
                </div>
              )}

              <PennField
                label="Email"
                htmlFor="email"
                required
                error={emailForm.formState.errors.email?.message}
              >
                <PennInput
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.com"
                  error={!!emailForm.formState.errors.email}
                  {...emailForm.register("email")}
                />
              </PennField>

              {submitError && (
                <div
                  role="alert"
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {submitError}
                </div>
              )}

              <PennButton
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                disabled={working}
                className="mt-2 bg-penn-500"
              >
                {working ? "Sending code…" : "Send code"}
              </PennButton>

            </form>
          )}

          {step === "otp" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void verifyOtp(otp);
              }}
              noValidate
              className="space-y-5"
            >
              <PennField label="One-time code" htmlFor="otp" required>
                <OtpInput
                  length={OTP_LENGTH}
                  value={otp}
                  onChange={handleOtpChange}
                  onComplete={handleOtpComplete}
                  autoFocus
                  error={!!submitError}
                  disabled={working}
                />
              </PennField>

              {resendNotice && (
                <div
                  role="status"
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                >
                  {resendNotice}
                </div>
              )}

              {submitError && (
                <div
                  role="alert"
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {submitError}
                </div>
              )}

              <PennButton
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                disabled={working || otp.length !== OTP_LENGTH}
                className="mt-2 bg-penn-500"
              >
                {working ? "Verifying…" : "Verify and sign in"}
              </PennButton>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={working || resendCooldown > 0}
                  className="text-penn-600 hover:text-penn-700 disabled:text-gray-400 font-medium"
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : "Resend code"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setSubmitError(null);
                    setResendNotice(null);
                    setOtp("");
                    otpAutoSubmittedRef.current = false;
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Use a different email
                </button>
              </div>
            </form>
          )}

          <PennText
            as="p"
            size="sm"
            align="center"
            className="mt-6 text-gray-500"
          >
            Don't have an account?{" "}
            <PennLink to="/register" size="sm" font="medium">
              Register
            </PennLink>
          </PennText>
        </div>
      </div>
    </div>
  );
};

// AUTH_MODE picks the front door once, at module load: the value is deployment
// config, not something that changes between renders.
export const LoginPage = () =>
  OIDC_ENABLED ? <OidcLoginPage /> : <CognitoLoginPage />;
